/**
 * SectorContext — expõe o SectorProfile do tenant logado.
 *
 * Uso:
 *   const { profile, labels, isClinic, isAutomotive, t, loading } = useSector();
 *
 *   <h1>Clientes e {labels.assetPlural(true)}</h1>
 *   {isAutomotive && <Vehicles />}
 *
 * Fonte: GET /api/sector/me — retornado UMA vez por sessão, cache
 * em memória (React state) + fallback silencioso para "automotive" se
 * a chamada falhar (garante zero regressão em produção).
 *
 * IMPORTANTE: labels.* SEMPRE devolvem strings válidas (nunca undefined).
 * A UI existente pode migrar incrementalmente — se um componente ainda
 * usa "Veículo" hardcoded, funciona; quando migrado para labels.assetSingular,
 * passa a se adaptar automaticamente.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

// Fallback default = automotive. Nunca deixamos labels undefined — se a
// API estiver fora, a UI segue como oficina, exatamente como hoje.
const DEFAULT_PROFILE = {
  code: "automotive",
  display_name: "Automotivo",
  asset_singular: "veículo",
  asset_plural: "veículos",
  service_singular: "troca de óleo",
  default_interval_months: 12,
  default_interval_km: 10000,
  enables_km_filter: true,
  enables_specialty_filter: false,
  enables_health_plan_filter: false,
  csv_extra_columns: ["Placa", "Marca", "Modelo", "Ano", "KM", "Combustível"],
  requires_stop_word_optout: true,
  is_clinic: false,
  is_automotive: true,
};

const SectorContext = createContext(null);

// Capitaliza a primeira letra respeitando UTF-8 (á, é, ç…)
function cap(s) {
  if (!s) return "";
  return s.charAt(0).toLocaleUpperCase("pt-BR") + s.slice(1);
}

// v0.98 · Fev/2026 — Sector Preview Mode
// LocalStorage key. Quando presente, sobrescreve o profile do tenant
// pela versão retornada por /api/sector/preview/{code}. Somente master
// ou sessão impersonated conseguem ativar (verificação server-side).
const PREVIEW_STORAGE_KEY = "agentus_sector_preview_code";

export function getSectorPreviewCode() {
  try {
    return localStorage.getItem(PREVIEW_STORAGE_KEY) || null;
  } catch (_e) {
    return null;
  }
}

export function setSectorPreviewCode(code) {
  try {
    if (code) {
      localStorage.setItem(PREVIEW_STORAGE_KEY, code);
    } else {
      localStorage.removeItem(PREVIEW_STORAGE_KEY);
    }
    // Broadcast pra outras abas E dentro da mesma aba (via CustomEvent)
    window.dispatchEvent(new CustomEvent("agentus:sector-preview-changed", {
      detail: { code },
    }));
  } catch (_e) { /* silencioso */ }
}

export function SectorProvider({ children }) {
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [loading, setLoading] = useState(true);
  const [previewCode, setPreviewCode] = useState(() => getSectorPreviewCode());

  // Escuta troca de preview em runtime (do switcher no topbar)
  useEffect(() => {
    const handler = (e) => setPreviewCode(e?.detail?.code || null);
    window.addEventListener("agentus:sector-preview-changed", handler);
    return () => window.removeEventListener("agentus:sector-preview-changed", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Se há preview ativo, busca esse setor. Fallback silencioso
        // pro tenant real se der 403 (sessão sem permissão de preview).
        const url = previewCode
          ? `/sector/preview/${encodeURIComponent(previewCode)}`
          : "/sector/me";
        const { data } = await api.get(url);
        if (!cancelled && data && typeof data === "object") {
          setProfile({ ...DEFAULT_PROFILE, ...data });
        }
      } catch (_e) {
        // Preview 403 → limpa flag e cai pro /me. Evita loop infinito.
        if (previewCode) {
          setSectorPreviewCode(null);
          try {
            const { data } = await api.get("/sector/me");
            if (!cancelled && data) {
              setProfile({ ...DEFAULT_PROFILE, ...data });
            }
          } catch (_e2) { /* mantém default automotive */ }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [previewCode]);

  const value = useMemo(() => {
    const p = profile;
    const labels = {
      assetSingular: (capitalize = false) =>
        capitalize ? cap(p.asset_singular) : p.asset_singular,
      assetPlural: (capitalize = false) =>
        capitalize ? cap(p.asset_plural) : p.asset_plural,
      serviceSingular: (capitalize = false) =>
        capitalize ? cap(p.service_singular) : p.service_singular,
    };
    // Helper t() — açúcar sintático pra strings compostas.
    const t = (key, opts = {}) => {
      const cap_ = !!opts.capitalize;
      switch (key) {
        case "asset_singular": return labels.assetSingular(cap_);
        case "asset_plural":   return labels.assetPlural(cap_);
        case "service_singular": return labels.serviceSingular(cap_);
        default: return key;
      }
    };
    return {
      profile: p,
      // v0.99.42 · Fev/2026 — expõe `code` no nível raiz do hook
      // pra evitar `sector.profile?.code` verboso em outras páginas.
      // Ex.: Integrations.jsx filtra ERPs por `sector.code`.
      code: p.code || "",
      labels,
      t,
      loading,
      is_clinic: !!p.is_clinic,
      isAutomotive: !!p.is_automotive,
      enablesKm: !!p.enables_km_filter,
      enablesSpecialty: !!p.enables_specialty_filter,
      enablesHealthPlan: !!p.enables_health_plan_filter,
      // v0.99.49 — Mandato NÃO expõe aba "ativo" na página de cidadãos.
      // Representantes é entidade institucional, não ativo do cliente.
      // Igreja também não — ministério é um atributo do membro, não um
      // ativo que precisa de CRUD dedicado dentro de /clientes.
      enablesAssetTab: !!p.enables_asset_tab && !p.is_mandate && !p.is_church,
      isClinic: !!p.is_clinic,
      // v0.98 SPRINT 2 · Fev/2026 — flag GOV (Gestão Pública).
      isGov: !!p.is_gov,
      // v0.99.9 · Fev/2026 — flag GYM (Academia / Studio).
      isGym: !!p.is_gym,
      // v0.99.19 · Fev/2026 — flags dos verticais restantes.
      // Habilitam adaptação de linguagem sem hard-coding automotivo.
      isRestaurant: !!p.is_restaurant,
      isSalon: !!p.is_salon,
      isPetshop: !!p.is_petshop,
      isPharmacy: !!p.is_pharmacy,
      isSchool: !!p.is_school,
      isAccountingFirm: !!p.is_accounting_firm,
      isCondominium: !!p.is_condominium,
      // v0.99.41 · Fev/2026 — flag Igreja.
      // Habilita sidebar pastoral com Pedidos de Oração, Células,
      // Ministérios, Aniversariantes e Devocional.
      isChurch: !!p.is_church,
      // v0.99.46 · Fev/2026 — flag Mandato & Governo.
      // Habilita sidebar de Representantes, KB por mandato e Demandas,
      // além dos labels dos funcionários proativos específicos.
      isMandate: !!p.is_mandate,
      previewCode,             // v0.98 — código do setor em preview (ou null)
      isPreviewMode: !!previewCode,
    };
  }, [profile, loading, previewCode]);

  return (
    <SectorContext.Provider value={value}>
      {children}
    </SectorContext.Provider>
  );
}

export function useSector() {
  const ctx = useContext(SectorContext);
  if (!ctx) {
    // Fora do provider (ex: página de login) — devolve default seguro.
    // Nunca lança, pra não quebrar telas legadas antes do provider
    // ser plugado.
    return {
      profile: DEFAULT_PROFILE,
      code: DEFAULT_PROFILE.code || "automotive",
      labels: {
        assetSingular: (c) => c ? "Veículo" : "veículo",
        assetPlural: (c) => c ? "Veículos" : "veículos",
        serviceSingular: (c) => c ? "Troca de óleo" : "troca de óleo",
      },
      t: (k, o = {}) => {
        const map = {
          asset_singular: o.capitalize ? "Veículo" : "veículo",
          asset_plural: o.capitalize ? "Veículos" : "veículos",
          service_singular: o.capitalize ? "Troca de óleo" : "troca de óleo",
        };
        return map[k] || k;
      },
      loading: false,
      isClinic: false,
      isAutomotive: true,
      enablesKm: true,
      enablesSpecialty: false,
      enablesHealthPlan: false,
      enablesAssetTab: true,
      isGov: false,
      isGym: false,
      isRestaurant: false,
      isSalon: false,
      isPetshop: false,
      isPharmacy: false,
      isSchool: false,
      isAccountingFirm: false,
      isCondominium: false,
      isChurch: false,
    };
  }
  return ctx;
}
